import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

export default function TwoFactorSetupFlow({ onComplete, onCancel }) {
  const [phase, setPhase] = useState('loading'); // loading | verify | backup-codes
  const [qrCode, setQrCode] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const startSetup = async () => {
      setError('');
      try {
        const data = await api.setup2FA();
        setQrCode(data.qrCodeDataUrl);
        setManualKey(data.manualEntryKey);
        setPhase('verify');
      } catch (err) {
        setError(err.message);
      }
    };
    startSetup();
  }, []);

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.verifySetup2FA(code.trim());
      setBackupCodes(data.backupCodes);
      setPhase('backup-codes');
      setCode('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (phase === 'loading') {
    return <p style={styles.helpText}>Generating your QR code...</p>;
  }

  if (phase === 'verify') {
    return (
      <form onSubmit={confirmSetup} style={styles.setupFlow}>
        <p style={styles.helpText}>
          Scan this QR code with your authenticator app, then enter the
          6-digit code it generates.
        </p>
        {qrCode && <img src={qrCode} alt="2FA QR Code" style={styles.qr} />}
        <p style={styles.manualKeyText}>
          Can't scan? Enter this key manually: <code>{manualKey}</code>
        </p>
        <input
          style={styles.codeInput}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          autoComplete="one-time-code"
          inputMode="numeric"
          pattern="[0-9]*"
          autoFocus
          required
        />
        {error && <p style={styles.error}>{error}</p>}
        <div style={styles.btnRow}>
          {onCancel && (
            <button type="button" style={styles.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          )}
          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading ? '...' : 'Verify & Enable'}
          </button>
        </div>
      </form>
    );
  }

  // phase === 'backup-codes'
  return (
    <div style={styles.setupFlow}>
      <p style={styles.helpText}>
        <strong style={{ color: '#fbbf24' }}>Save these backup codes now.</strong>{' '}
        Each one can be used once to sign in if you lose access to your
        authenticator app. They won't be shown again.
      </p>
      <div style={styles.backupCodesBox}>
        {backupCodes.map((c) => (
          <code key={c} style={styles.backupCode}>{c}</code>
        ))}
      </div>
      <button style={styles.primaryBtn} onClick={onComplete}>
        I've saved these codes
      </button>
    </div>
  );
}

const styles = {
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  primaryBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  cancelBtn: {
    background: 'transparent',
    color: '#6b7280',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 1rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  setupFlow: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  qr: {
    width: '180px',
    height: '180px',
    alignSelf: 'center',
    background: '#fff',
    padding: '8px',
    borderRadius: '8px',
  },
  manualKeyText: {
    color: '#4b5563',
    fontSize: '0.7rem',
    textAlign: 'center',
    wordBreak: 'break-all',
  },
  codeInput: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.7rem',
    color: '#fff',
    fontSize: '1.1rem',
    textAlign: 'center',
    letterSpacing: '0.2em',
    fontFamily: "'DM Mono', monospace",
  },
  error: { color: '#ef4444', fontSize: '0.75rem', margin: 0 },
  btnRow: { display: 'flex', gap: '0.5rem' },
  backupCodesBox: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '1rem',
  },
  backupCode: {
    color: '#00d4aa',
    fontSize: '0.85rem',
    fontFamily: "'DM Mono', monospace",
    textAlign: 'center',
  },
};
